/**
 * Composite hook for fetching dashboard data in parallel
 * Uses Promise.allSettled to fetch wallet balance and initiative stats concurrently
 * @module hooks/use-dashboard-data
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { getWalletBalance } from '@/lib/api/wallet';
import { getInitiativeStatistics } from '@/lib/api/initiatives';
import { getCached, setCached, isFresh } from '@/lib/api/cache';
import type { WalletBalance } from '@/types/wallet';
import type { InitiativeStatistics } from '@/types/initiative';

export interface UseDashboardDataReturn {
  walletBalance: WalletBalance | null;
  initiativeStats: InitiativeStatistics | null;
  loading: boolean;
  walletError: string | null;
  statsError: string | null;
  refetch: () => Promise<void>;
}

const WALLET_CACHE_KEY = 'wallet-balance';
const STATS_CACHE_KEY = 'initiative-stats';
const CACHE_TTL = 60_000;

export function useDashboardData(): UseDashboardDataReturn {
  const [walletBalance, setWalletBalance] = useState<WalletBalance | null>(null);
  const [initiativeStats, setInitiativeStats] = useState<InitiativeStatistics | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [walletError, setWalletError] = useState<string | null>(null);
  const [statsError, setStatsError] = useState<string | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const fetchDashboardData = useCallback(async () => {
    // Stale-while-revalidate: serve cached data immediately if present
    const cachedWallet = getCached<WalletBalance>(WALLET_CACHE_KEY);
    const cachedStats = getCached<InitiativeStatistics>(STATS_CACHE_KEY);

    if (cachedWallet) setWalletBalance(cachedWallet);
    if (cachedStats) setInitiativeStats(cachedStats);

    // Skip fetches if both are still fresh
    const walletFresh = isFresh(WALLET_CACHE_KEY);
    const statsFresh = isFresh(STATS_CACHE_KEY);

    if (walletFresh && statsFresh) {
      setLoading(false);
      return;
    }

    setLoading(true);

    const [walletRes, statsRes] = await Promise.allSettled([
      walletFresh ? Promise.resolve(null) : getWalletBalance(),
      statsFresh ? Promise.resolve(null) : getInitiativeStatistics(),
    ]);

    if (!mountedRef.current) return;

    // Handle wallet result
    if (!walletFresh) {
      if (walletRes.status === 'fulfilled' && walletRes.value?.success && walletRes.value.data) {
        setCached(WALLET_CACHE_KEY, walletRes.value.data, CACHE_TTL);
        setWalletBalance(walletRes.value.data);
        setWalletError(null);
      } else if (walletRes.status === 'rejected') {
        setWalletError(
          walletRes.reason instanceof Error
            ? walletRes.reason.message
            : 'Failed to load wallet balance'
        );
      } else if (walletRes.status === 'fulfilled' && walletRes.value && !walletRes.value.success) {
        setWalletError(walletRes.value.message || 'Failed to load wallet balance');
      }
    }

    // Handle stats result
    if (!statsFresh) {
      if (statsRes.status === 'fulfilled' && statsRes.value?.success && statsRes.value.data) {
        setCached(STATS_CACHE_KEY, statsRes.value.data, CACHE_TTL);
        setInitiativeStats(statsRes.value.data);
        setStatsError(null);
      } else if (statsRes.status === 'rejected') {
        setStatsError(
          statsRes.reason instanceof Error
            ? statsRes.reason.message
            : 'Failed to load initiative statistics'
        );
      } else if (statsRes.status === 'fulfilled' && statsRes.value && !statsRes.value.success) {
        setStatsError(statsRes.value.message || 'Failed to load initiative statistics');
      }
    }

    if (mountedRef.current) setLoading(false);
  }, []);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  return {
    walletBalance,
    initiativeStats,
    loading,
    walletError,
    statsError,
    refetch: fetchDashboardData,
  };
}
